/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type Ref, type RefAttributes, useMemo, useRef } from 'react';

import type { RouterLinkComponentProps } from '@atlaskit/app-provider';
import { cssMap, jsx } from '@atlaskit/css';
import { LinkMenuItem as AtlasLinkMenuItem } from '@atlaskit/side-nav-items/link-menu-item';

import { forwardRefWithGeneric } from '../../utils/forward-ref-with-generic';
import mergeRefs from '../../utils/merge-refs';
import { ListItem } from '../list-item';
import { MenuItemBase } from '../menu-item';
import type {
	MenuItemCommonProps,
	MenuItemLinkOrButtonCommonProps,
	MenuItemOnClick,
	MenuItemSlots,
} from '../menu-item/types';

const listItemStyles = cssMap({
	root: {
		/**
		 * Setting a large scroll margin to prevent _horizontally_ scrolling the selected menu item into view.
		 * `scrollIntoView` will scroll on both axes and does not allow completely opting out of scrolling for a particular axis.
		 *
		 * We have specifically chosen to prevent horizontal scrolling as deeply nested menu items are not a common use case, and
		 * horizontal scrolling can cause the the sidebar to look inconsistent on load.
		 *
		 * We are using a large static value to here to handle all realistic nesting levels. As an alternative we could dynamically
		 * calculate this based on the current nesting level, but it isn't necessary and would be a performance hit.
		 */
		scrollMarginInline: '1000px',
	},
});

export type LinkMenuItemProps<RouterLinkConfig extends Record<string, unknown> = never> =
	MenuItemLinkOrButtonCommonProps &
		RouterLinkComponentProps<RouterLinkConfig> & {
			/**
			 * The native `target` attribute for the anchor element.
			 */
			target?: HTMLAnchorElement['target'];
			/**
			 * Whether the menu item is selected.
			 */
			isSelected?: boolean;

			/**
			 * Called when the user has clicked on the trigger content.
			 */
			onClick?: MenuItemOnClick<HTMLAnchorElement>;
			/**
			 * Whether the ADS navigation (left-nav) is enabled rather than the in-component navigation
			 */
			isADSNavigationEnabled: boolean;
			/**
			 * Size variant for the menu item.
			 * - 'default': 2rem (32px) height
			 * - 'small': 3.4rem (54px) height with custom padding
			 * - 'large': 3.75rem (60px) minimum height with custom padding
			 */
			size?: 'default' | 'small' | 'large';
			/**
			 * Whether the new all conversation screen is enabled
			 */
			isNewAllConversationScreenEnabled?: boolean;
			/**
			 * When true, disables the inner :active background on the anchor/button.
			 * Useful when the container already provides its own pressed/selected highlight.
			 */
			noActiveBackground?: boolean;
		};

const LinkMenuItemNoRef = <RouterLinkConfig extends Record<string, unknown> = never>(
	{
		testId,
		actions,
		children,
		description,
		elemAfter,
		elemBefore,
		href,
		target,
		actionsOnHover,
		isSelected,
		onClick,
		interactionName,
		isContentTooltipDisabled,
		visualContentRef,
		listItemRef,
		level = 0,
		isADSNavigationEnabled,
		size,
		isNewAllConversationScreenEnabled,
		noActiveBackground,
	}: LinkMenuItemProps<RouterLinkConfig>,
	forwardedRef?: Ref<HTMLAnchorElement>,
) => {
	const itemRef = useRef<HTMLDivElement>(null);

	const ref = useMemo(() => {
		return mergeRefs([itemRef, listItemRef ?? null]);
	}, [itemRef, listItemRef]);

	const commonProps = {
		testId,
		description,
		actions,
		elemAfter,
		href,
		target,
		actionsOnHover,
		isSelected,
		onClick,
		ref: forwardedRef,
		visualContentRef,
		interactionName,
		isContentTooltipDisabled,
	};

	return isNewAllConversationScreenEnabled ? (
		<ListItem ref={ref} xcss={listItemStyles.root}>
			<MenuItemBase
				{...commonProps}
				level={level}
				size={size}
				elemBefore={elemBefore}
				noActiveBackground={noActiveBackground}
			>
				{children}
			</MenuItemBase>
		</ListItem>
	) : isADSNavigationEnabled ? (
		//We must pass the elemBefore prop separately and cast it because the forked type definition and
		// ADS type definition COLLAPSE_ELEM_BEFORE_TYPE are each created using unique symbols
		<AtlasLinkMenuItem
			{...commonProps}
			description={typeof description === 'string' ? description : undefined}
			elemBefore={elemBefore as React.ReactNode}
		>
			{children}
		</AtlasLinkMenuItem>
	) : (
		<ListItem ref={ref} xcss={listItemStyles.root}>
			<MenuItemBase
				{...commonProps}
				level={level}
				// TODO Always use "open in new window" icon when `openInNewWindow` prop is true
				elemBefore={elemBefore}
			>
				{children}
			</MenuItemBase>
		</ListItem>
	);
};

/**
 * LinkMenuItem
 *
 * A menu item link. It should be used within a `ul`.
 */
export const LinkMenuItem: <RouterLinkConfig extends Record<string, unknown> = never>(
	props: MenuItemCommonProps &
		MenuItemSlots & {
			description?: React.ReactNode;
			listItemRef?: React.Ref<HTMLDivElement>;
		} & RouterLinkComponentProps<RouterLinkConfig> & {
			/**
			 * The native `target` attribute for the anchor element.
			 */
			target?: HTMLAnchorElement['target'];
			/**
			 * Whether the menu item is selected.
			 */
			isSelected?: boolean;
			/**
			 * Called when the user has clicked on the trigger content.
			 */
			onClick?: MenuItemOnClick<HTMLAnchorElement>;
			/**
			 * Whether the ADS navigation (left-nav) is enabled rather than the in-component navigation
			 */
			isADSNavigationEnabled: boolean;
			/**
			 * Size variant for the menu item.
			 * - 'default': 2rem (32px) height
			 * - 'large': 3.75rem (60px) minimum height with custom padding
			 */
			size?: 'default' | 'small' | 'large';
			/**
			 * Whether the new all conversation screen is enabled
			 */
			isNewAllConversationScreenEnabled?: boolean;
			/**
			 * When true, disables the inner :active background on the anchor/button.
			 * Useful when the container already provides its own pressed/selected highlight.
			 */
			noActiveBackground?: boolean;
		} & RefAttributes<HTMLAnchorElement>,
) => React.ReactElement | null = forwardRefWithGeneric(LinkMenuItemNoRef);
